import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Star, Trash2, Save, Edit2 } from 'lucide-react';
import { api } from '../context/AuthContext';
import './Settings.css';

const StarRating = ({ value, onChange, size = 22, testid }) => (
  <div style={{ display: 'inline-flex', gap: 4 }} data-testid={testid}>
    {[1,2,3,4,5].map(i => (
      <button
        key={i}
        type="button"
        onClick={() => onChange && onChange(i)}
        style={{ background: 'none', border: 'none', cursor: onChange ? 'pointer' : 'default', padding: 2 }}
      >
        <Star size={size} fill={i <= value ? '#D97706' : 'transparent'} color={i <= value ? '#D97706' : '#9CA3AF'} />
      </button>
    ))}
  </div>
);

const ReviewsPage = () => {
  const navigate = useNavigate();
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // rating.id being edited
  const [draft, setDraft] = useState({ teachability_rating: 5, review_text: '' });

  const load = async () => {
    try {
      const d = await api('/api/ratings/mine');
      setRatings(d.ratings || []);
    } catch { setRatings([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startEdit = (r) => {
    setEditing(r.id);
    setDraft({ teachability_rating: r.teachability_rating || Math.round(r.overall_rating || 5), review_text: r.review_text || '' });
  };

  const saveEdit = async (r) => {
    try {
      await api(`/api/ratings/${r.id}`, {
        method: 'PUT',
        body: JSON.stringify({ teachability_rating: draft.teachability_rating, review_text: draft.review_text }),
      });
      toast.success('Review updated.');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm('Delete this review? The teacher\'s average will be recalculated.')) return;
    try {
      await api(`/api/ratings/${r.id}`, { method: 'DELETE' });
      toast.success('Review deleted.');
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="settings-container" data-testid="reviews-page">
      <div className="settings-header">
        <button className="settings-back-btn" onClick={() => navigate('/settings')} data-testid="reviews-back-btn">
          <ArrowLeft size={24} />
        </button>
        <h1 className="settings-title">Rating &amp; Review</h1>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Loading…</div>
      ) : ratings.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#4B5563' }}>
          <Star size={48} strokeWidth={1.4} style={{ margin: '0 auto 16px', display: 'block', opacity: 0.5 }} />
          <p style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', margin: 0 }}>No reviews yet</p>
          <p style={{ fontSize: 14, marginTop: 8 }}>After each session you can rate the mentor here.</p>
        </div>
      ) : (
        <div style={{ padding: 12 }}>
          {ratings.map(r => (
            <div key={r.id} data-testid={`my-review-${r.id}`} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <img src={r.creator?.avatar_url || `https://i.pravatar.cc/150?u=${r.creator?.id}`} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 600, color: '#1F2937' }}>{r.creator?.full_name || 'Mentor'}</p>
                  <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : ''}
                  </p>
                </div>
                {editing !== r.id && (
                  <>
                    <button onClick={() => startEdit(r)} data-testid={`edit-review-${r.id}`} style={{ background: 'none', border: 'none', color: '#2C4E46', cursor: 'pointer', padding: 6 }}>
                      <Edit2 size={18} />
                    </button>
                    <button onClick={() => remove(r)} data-testid={`delete-review-${r.id}`} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', padding: 6 }}>
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
              </div>

              {editing === r.id ? (
                <>
                  <StarRating value={draft.teachability_rating} onChange={(v) => setDraft(p => ({ ...p, teachability_rating: v }))} testid={`edit-stars-${r.id}`} />
                  <textarea
                    value={draft.review_text}
                    onChange={(e) => setDraft(p => ({ ...p, review_text: e.target.value }))}
                    placeholder="Update your review…"
                    rows={3}
                    data-testid={`edit-text-${r.id}`}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, marginTop: 8, fontFamily: 'inherit', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button onClick={() => setEditing(null)} style={{ flex: 1, padding: '10px', borderRadius: 999, border: '1px solid #d1d5db', background: '#fff', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                    <button onClick={() => saveEdit(r)} data-testid={`save-review-${r.id}`} style={{ flex: 1, padding: '10px', borderRadius: 999, border: 'none', background: '#2C4E46', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <Save size={14} /> Save
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <StarRating value={Math.round(r.overall_rating || 0)} size={18} />
                  {r.review_text && <p style={{ color: '#374151', fontSize: 14, margin: '8px 0 0', lineHeight: 1.5 }}>{r.review_text}</p>}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsPage;
